import { ConvexError } from "convex/values";
import { captureConvexError } from "@/lib/sentry";
import { toast } from "@/lib/toast";

export function getConvexErrorCode(error: unknown): string | null {
	if (!(error instanceof ConvexError)) return null;
	const data = error.data;
	if (typeof data !== "object" || data === null) return null;
	if (!("code" in data)) return null;
	const { code } = data as { code: unknown };
	return typeof code === "string" ? code : null;
}

type ConvexErrorData = {
	code?: string;
	message?: string;
	currentCount?: number;
	limit?: number;
	retryAfter?: number;
	retryAfterMs?: number;
	cost?: number;
};

const SKIP_CAPTURE_CODES = new Set([
	"TOTP_REQUIRED",
	"TOTP_INVALID",
	"TOTP_RATE_LIMITED",
	"NOT_AUTHENTICATED",
	"MISSING_PERMISSION",
	"CANNOT_MODIFY_OWN_ROLE",
	"CANNOT_REMOVE_SELF",
	"CANNOT_REMOVE_CREATOR",
	"CANNOT_CHANGE_CREATOR_ROLE",
	"BRANCH_INACTIVE",
	"SUBSCRIPTION_EXPIRED",
	"ALREADY_A_MEMBER",
	"CONCURRENT_LIMIT_EXCEEDED",
	"RATE_LIMIT_EXCEEDED",
]);

export function handleConvexError(
	error: unknown,
	defaultMessage = "An unexpected error occurred",
) {
	if (error instanceof ConvexError) {
		const payload = error.data as string | ConvexErrorData;

		if (typeof payload === "string") {
			toast.error(payload);
			captureConvexError(error);
			return;
		}

		if (typeof payload !== "object" || payload === null) {
			toast.error(defaultMessage);
			captureConvexError(error);
			return;
		}

		switch (payload.code) {
			case "CONCURRENT_LIMIT_EXCEEDED":
				toast.error(payload.message || "Concurrent limit exceeded", {
					description: `Maximum ${payload.limit} requests allowed. Please wait for current operations to finish.`,
				});
				return;

			case "RATE_LIMIT_EXCEEDED": {
				const retrySecs = payload.retryAfter
					? Math.ceil(payload.retryAfter / 1000)
					: null;
				toast.error(payload.message || "Rate limit exceeded", {
					description: retrySecs
						? `Please wait ${retrySecs}s before trying again.`
						: "Please slow down and try again later.",
				});
				return;
			}

			case "TOTP_RATE_LIMITED": {
				const retrySecs = payload.retryAfterMs
					? Math.ceil(payload.retryAfterMs / 1000)
					: null;
				toast.error(payload.message || "Too many attempts", {
					description: retrySecs
						? `Try again in ${retrySecs}s.`
						: "Try again in a moment.",
				});
				return;
			}

			default:
				toast.error(payload.message || defaultMessage);
				if (!payload.code || !SKIP_CAPTURE_CODES.has(payload.code)) {
					captureConvexError(error, payload.code);
				}
				return;
		}
	}

	if (error instanceof Error) {
		toast.error(error.message || defaultMessage);
		captureConvexError(error);
	} else {
		toast.error(defaultMessage);
		captureConvexError(error);
	}
}
